import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import {
    Autocomplete,
    Backdrop,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    FormControlLabel,
    FormHelperText,
    ListItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers-pro';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Controller, FieldError, useWatch } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'usehooks-ts';
import { ZodIssueCode } from 'zod';
import { Locations } from '../../../backbone-unorganized/models';
import { FormTemplates, UsersV3 } from '../../../backbone-unorganized/shared/forms';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import { ErrorFooterAlert } from '../../shared/ErrorFooterAlert.tsx/ErrorFooterAlert';
import PageLayout from '../../shared/PageLayout';
import { useBackbone } from '../providers/BackboneProvider';
import { DeleteFollowUpActionModal } from './DeleteFollowUpActionModal';
import {
    InitialFollowUpActionType,
    InitialFollowUpActionSchema,
    MobileFormContext,
    TemporalFollowUpActionType,
    AllReplyTypes,
    FollowUpActionMode,
    FollowUpActionFileType
} from './FollowUpActions.types';
import { FollowUpActionsGallery } from './FollowUpActionsGallery';
import { SlimLocation, LegacyApiModel, LocationsQueryParams, IdLabelPair, UsersQueryParams } from './types';

const useStyles = makeStyles()((theme) => {
    const { spacing, typography } = theme;

    return {
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: spacing(2),
            textTransform: 'none',
            backgroundColor: 'white'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        formContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            marginBottom: spacing(16),
            padding: spacing(2)
        },
        formInput: {
            marginTop: spacing(2),
            '& .MuiInputBase-input': {
                border: 'none',
                padding: `${spacing(2)} 0`,
                borderBottom: 'groove'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        textFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                border: 'none',
                background: 'none'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                fontSize: typography.body2.fontSize,
                color: 'black !important'
            }
        },
        dateFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                padding: `${spacing(2.5)} 0`,
                border: 'none',
                color: 'black !important'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                borderBottom: 'groove'
            },
            '& .MuiOutlinedInput-notchedOutline:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        autoSelect: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                minHeight: '36px',
                border: 'none',
                background: 'none',
                padding: `${spacing(2)} 0`,
                color: 'black !important'
            },
            '& .MuiFormControl-root': {
                height: 'auto'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        }
    };
});

export const toDate = (value: Date | string) => {
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
        const parsed = parseISO(value);
        return parsed;
    }
    return undefined;
};

const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const emptyFollowUpAction: InitialFollowUpActionType = {
    assigned_user: null,
    description: '',
    due_date: null,
    form: null,
    images: [],
    location: null,
    reply_type: AllReplyTypes[0],
    require_photo: false,
    title: ''
};

interface FollowUpActionsFormProps {
    initialFollowUpAction: TemporalFollowUpActionType;
    mode: FollowUpActionMode;
    onAddFollowUpAction: (fua: TemporalFollowUpActionType) => void;
    onDeleteFollowUpAction: (tempId: string) => void;
    onGoBack: () => void;
    open: boolean;
    selectedLocation: SlimLocation | null;
}

export const FollowUpActionForm = (props: FollowUpActionsFormProps) => {
    const {
        initialFollowUpAction,
        mode,
        onAddFollowUpAction,
        onDeleteFollowUpAction,
        onGoBack,
        open,
        selectedLocation
    } = props;
    const { classes } = useStyles();
    const { uploadFilesToS3, allowAccessToReportingLocations, formReplyEnabled, getS3URL } =
        useBackbone<MobileFormContext>();
    const { enqueueSnackbar } = useSnackbar();
    const [locations, setLocations] = useState<SlimLocation[]>([]);
    const [assignees, setAssignees] = useState<{ id: number; display_name: string }[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const isEditMode = mode === 'edit';

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useMuiForm<InitialFollowUpActionType>({
        resolver: zodResolver(InitialFollowUpActionSchema),
        defaultValues: emptyFollowUpAction
    });

    const requiredOrderedFieldsTask = ['title', 'description', 'due_date', 'location'];
    const requiredOrderedFieldsForm = ['title', 'description', 'due_date', 'form', 'location'];

    const [locationSearch, setLocationSearch] = useState('');
    const debouncedLocationSearch = useDebounce(locationSearch, 200);
    const [uploadedFiles, setUploadedFiles] = useState<FollowUpActionFileType[]>([]);
    const [assigneeSearch, setAssigneeSearch] = useState('');
    const debouncedAssigneeSearch = useDebounce(assigneeSearch, 200);
    const [formSearch, setFormSearch] = useState('');
    const debouncedFormSearch = useDebounce(formSearch, 200);
    const [forms, setForms] = useState<IdLabelPair[]>([]);
    const [showSpinner, setShowSpinner] = useState(false);

    const getError = (error?: FieldError) => {
        const zodErrorCodeMap: Record<string, string | undefined> = {
            invalid_type: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_small: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_big: _t('mobile_mobileform_add_follow_up_actions_forms_too_many_characters')
        } satisfies Partial<Record<ZodIssueCode, string>>;

        return error
            ? {
                  error: true,
                  helperText: zodErrorCodeMap[error.type] ?? _t('mobile_mobileform_add_follow_up_actions_forms_generic')
              }
            : { error: false };
    };

    const replyType = useWatch({ name: 'reply_type', control });

    const { field: assigneeField } = useMuiController({ control, name: 'assigned_user' });
    const { field: descriptionField } = useMuiController({ control, name: 'description' });
    const { field: dueDateField } = useMuiController({ control, name: 'due_date' });
    const { field: formField } = useMuiController({ control, name: 'form' });
    const { field: locationField } = useMuiController({
        control,
        name: 'location',
        defaultValue: selectedLocation ?? undefined
    });
    const { field: requirePhotoField } = useMuiController({ control, name: 'require_photo' });
    const { field: titleField } = useMuiController({ control, name: 'title' });

    const requestParams: LocationsQueryParams = allowAccessToReportingLocations
        ? {
              // For admins and auditors
              allow_cross_company_access: allowAccessToReportingLocations,
              order_by: 'name',
              search: debouncedLocationSearch
          }
        : {
              // For everyone else, including FBCs
              include_fbc_locations: true,
              order_by: 'name',
              search: debouncedLocationSearch
          };

    const userRequestParams: UsersQueryParams = allowAccessToReportingLocations
        ? { allow_cross_company_access: allowAccessToReportingLocations }
        : {};

    useEffect(() => {
        const mobileLocations = new Locations([], requestParams);
        mobileLocations.fetch({
            success: function () {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const locationData = mobileLocations.models.map((model: any) => ({
                    id: model.get('id'),
                    label: model.get('name') ?? '',
                    time_zone: model.get('time_zone')
                }));
                setLocations(locationData);
                setLoading(false);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_locations')), {
                    variant: 'error'
                });
                setLoading(false);
            }
        });
    }, [debouncedLocationSearch]);

    useEffect(() => {
        const mobileAssignees = new UsersV3([], userRequestParams);
        mobileAssignees.paginator.set('search', debouncedAssigneeSearch);

        mobileAssignees.fetch({
            success: function () {
                const assigneeData = mobileAssignees.models.map((model: LegacyApiModel) => ({
                    id: model.get('id'),
                    display_name: model.get('display_name')
                }));
                setAssignees(assigneeData);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_users')), {
                    variant: 'error'
                });
            }
        });
    }, [debouncedAssigneeSearch]);

    useEffect(() => {
        if (!formReplyEnabled) {
            return;
        }
        const formTemplates = new FormTemplates([], { search: debouncedFormSearch });
        formTemplates.fetch({
            success: function () {
                const formsData = formTemplates.models.map((model: LegacyApiModel) => ({
                    id: model.get('id'),
                    label: model.get('title') ?? ''
                }));
                setForms(formsData);
                setLoading(false);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_forms')), {
                    variant: 'error'
                });
                setLoading(false);
            }
        });
    }, [debouncedFormSearch, formReplyEnabled]);
    const [requiredOrderedFields, setRequiredOrderedFields] = useState(requiredOrderedFieldsTask);
    useEffect(() => {
        setRequiredOrderedFields(replyType === 'form' ? requiredOrderedFieldsForm : requiredOrderedFieldsTask);
    }, [replyType]);

    useEffect(() => {
        if (isEditMode && initialFollowUpAction) {
            const correctDate = toDate(initialFollowUpAction.due_date!);
            reset({
                assigned_user: initialFollowUpAction.assigned_user,
                description: initialFollowUpAction.description,
                due_date: correctDate,
                form: initialFollowUpAction.form ?? null,
                images: initialFollowUpAction.images ?? [],
                location: initialFollowUpAction.location,
                reply_type: initialFollowUpAction.reply_type,
                require_photo: initialFollowUpAction.require_photo ?? false,
                title: initialFollowUpAction.title
            });
            if (initialFollowUpAction.images) {
                setUploadedFiles(initialFollowUpAction.images);
            }
        } else {
            if (selectedLocation) {
                reset({ ...emptyFollowUpAction, location: selectedLocation });
            } else {
                reset(emptyFollowUpAction);
            }

            setUploadedFiles([]);
        }
    }, [initialFollowUpAction, open, isEditMode, selectedLocation]);

    const handleDiscardOrDelete = () => {
        if (isEditMode) {
            setShowDeleteModal(true);
        } else {
            onGoBack();
        }
    };

    const handleDeleteFollowUpAction = () => {
        onDeleteFollowUpAction(initialFollowUpAction.temp_id!);
        setShowDeleteModal(false);
    };

    const handleOnUploadImages = (images: FollowUpActionFileType[]) => {
        setUploadedFiles((prevImages) => [...prevImages, ...images]);
    };

    const handleOnDeleteUploadedImage = (indexToRemove: number) => {
        setUploadedFiles((prevImages) => prevImages.filter((_, index) => indexToRemove !== index));
    };

    const onSubmit = handleSubmit((data) => {
        let tempData: TemporalFollowUpActionType = { ...data, images: uploadedFiles };

        if (isEditMode) {
            tempData = { ...tempData, temp_id: initialFollowUpAction.temp_id };
        }

        onAddFollowUpAction(tempData);
        setUploadedFiles([]);
    });

    return (
        <>
            <Dialog
                fullScreen
                onClose={onGoBack}
                open={open}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'hidden'
                        }
                    }
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <PageLayout>
                        {{
                            appBar: (
                                <AppBar
                                    onGoBack={onGoBack}
                                    title={_t('mobile_mobileform_add_follow_up_actions_add_follow_up')}
                                />
                            ),
                            content: (
                                <div className={classes.formContainer}>
                                    {showSpinner && (
                                        <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.appBar - 1 }}>
                                            <CircularProgress />
                                        </Backdrop>
                                    )}
                                    <Stack component="form" onSubmit={onSubmit}>
                                        {formReplyEnabled && (
                                            <Controller
                                                name="reply_type"
                                                control={control}
                                                render={({ field }) => {
                                                    const { value, onChange } = field;
                                                    return (
                                                        <ToggleButtonGroup
                                                            color="primary"
                                                            exclusive
                                                            fullWidth
                                                            onChange={(_, newValue) => {
                                                                if (newValue) {
                                                                    onChange(newValue);
                                                                }
                                                            }}
                                                            sx={{ paddingBottom: 2 }}
                                                            size="large"
                                                            unselectable="off"
                                                            value={value}
                                                        >
                                                            <ToggleButton
                                                                aria-label={_t('mobile_common_task')}
                                                                value={AllReplyTypes[0]}
                                                            >
                                                                <CheckBoxOutlinedIcon />
                                                                <Typography paddingLeft={1}>
                                                                    {_t('mobile_common_task')}
                                                                </Typography>
                                                            </ToggleButton>
                                                            <ToggleButton
                                                                aria-label={_t('mobile_task_tab_form')}
                                                                value={AllReplyTypes[1]}
                                                            >
                                                                <AssignmentOutlinedIcon />
                                                                <Typography paddingLeft={1}>
                                                                    {_t('mobile_task_tab_form')}{' '}
                                                                </Typography>
                                                            </ToggleButton>
                                                        </ToggleButtonGroup>
                                                    );
                                                }}
                                            />
                                        )}
                                        <Controller
                                            name="images"
                                            control={control}
                                            render={({ field }) => {
                                                useEffect(() => {
                                                    field.onChange(uploadedFiles);
                                                }, [uploadedFiles, field.onChange]);

                                                return (
                                                    <FollowUpActionsGallery
                                                        getS3URL={getS3URL}
                                                        isReply={false}
                                                        onDeleteUploadedImage={handleOnDeleteUploadedImage}
                                                        onUploadFiles={handleOnUploadImages}
                                                        photoQualityAnalysis={false}
                                                        uploadedFiles={uploadedFiles}
                                                        uploadFilesToS3={uploadFilesToS3}
                                                        onLoadingChange={setShowSpinner}
                                                    />
                                                );
                                            }}
                                        />

                                        <TextField
                                            className={classes.formInput}
                                            id="title"
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_title')}
                                            required
                                            variant="filled"
                                            {...titleField}
                                            {...getError(errors.title)}
                                        />
                                        <TextField
                                            className={classes.textFieldInput}
                                            data-testid="controlled-input2"
                                            error={getError(errors.description).error}
                                            id="description"
                                            multiline
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_description')}
                                            required
                                            variant="filled"
                                            {...descriptionField}
                                        />
                                        {errors.description && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.description).helperText}
                                            </FormHelperText>
                                        )}
                                        {(!formReplyEnabled || replyType == 'task') && (
                                            <FormControlLabel
                                                sx={{ padding: 1 }}
                                                control={
                                                    <Checkbox
                                                        checked={requirePhotoField.value}
                                                        sx={{ marginLeft: 1 }}
                                                        {...requirePhotoField}
                                                    />
                                                }
                                                label={_t('mobile_mobileform_add_follow_up_actions_forms_photo')}
                                            />
                                        )}
                                        {replyType == 'form' && (
                                            <Autocomplete
                                                className={classes.autoSelect}
                                                disabled={loading}
                                                disablePortal
                                                getOptionKey={(option) => option.id}
                                                getOptionLabel={(option) => option.label}
                                                onChange={(_event, newValue) => {
                                                    formField.onChange(newValue);
                                                }}
                                                options={forms}
                                                value={formField.value}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={getError(errors.form as FieldError).error}
                                                        label={_t('mobile_task_tab_form')}
                                                        onBlur={() => setFormSearch('')}
                                                        onChange={(e) => setFormSearch(e.target.value)}
                                                        required
                                                        slotProps={{
                                                            htmlInput: {
                                                                ...params.inputProps,
                                                                required: true,
                                                                'data-testid': 'form-autocomplete'
                                                            }
                                                        }}
                                                        sx={{
                                                            height: (theme) => theme.spacing(15)
                                                        }}
                                                        value={formSearch}
                                                        variant="filled"
                                                    />
                                                )}
                                                renderOption={(renderProps, option) => (
                                                    <ListItem {...renderProps}>{option.label}</ListItem>
                                                )}
                                            />
                                        )}
                                        {replyType == 'form' && errors.form && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.form as FieldError).helperText}
                                            </FormHelperText>
                                        )}

                                        <MobileDatePicker
                                            className={classes.dateFieldInput}
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_due_date')}
                                            minDate={getToday()}
                                            slotProps={{
                                                textField: {
                                                    error: getError(errors.due_date).error,
                                                    id: 'due_date',
                                                    required: true,
                                                    slotProps: {
                                                        input: { endAdornment: <CalendarTodayIcon /> }
                                                    },
                                                    variant: 'filled'
                                                }
                                            }}
                                            value={dueDateField.value ?? null}
                                            onChange={(newDate) => {
                                                if (newDate) {
                                                    newDate.setHours(12, 0, 0, 0);
                                                }
                                                dueDateField.onChange(newDate);
                                            }}
                                        />
                                        {errors.due_date && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.due_date).helperText}
                                            </FormHelperText>
                                        )}

                                        <Autocomplete
                                            className={classes.autoSelect}
                                            disabled={loading}
                                            disablePortal
                                            getOptionKey={(option) => option.id}
                                            getOptionLabel={(option) => option.label}
                                            id="location"
                                            // Without isOptionEqualToValue we get an error on Safari/iOS
                                            isOptionEqualToValue={(option, value) =>
                                                value === null || option.id === value.id
                                            }
                                            onChange={(_event, newValue) => {
                                                locationField.onChange(newValue);
                                            }}
                                            options={locations}
                                            value={locationField.value}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={getError(errors.location as FieldError).error}
                                                    label={_t('mobile_common_location')}
                                                    onBlur={() => setLocationSearch('')}
                                                    onChange={(e) => setLocationSearch(e.target.value)}
                                                    required
                                                    slotProps={{
                                                        htmlInput: {
                                                            ...params.inputProps,
                                                            'data-testid': 'location-autocomplete'
                                                        }
                                                    }}
                                                    sx={{
                                                        height: (theme) => theme.spacing(15)
                                                    }}
                                                    value={locationSearch}
                                                    variant="filled"
                                                />
                                            )}
                                            renderOption={(renderProps, option) => (
                                                <ListItem {...renderProps}>{option.label}</ListItem>
                                            )}
                                        />
                                        {'location' in errors && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.location as FieldError).helperText}
                                            </FormHelperText>
                                        )}

                                        <Autocomplete
                                            className={classes.autoSelect}
                                            disablePortal
                                            getOptionKey={(option) => option.id}
                                            getOptionLabel={(option) => option.display_name}
                                            onChange={(_event, newValue) => {
                                                assigneeField.onChange(newValue);
                                            }}
                                            options={assignees}
                                            value={assigneeField.value ?? null}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={getError(errors.assigned_user as FieldError).error}
                                                    label={_t('mobile_common_assigned_to')}
                                                    onBlur={() => setAssigneeSearch('')}
                                                    onChange={(e) => setAssigneeSearch(e.target.value)}
                                                    slotProps={{
                                                        htmlInput: {
                                                            ...params.inputProps,
                                                            'data-testid': 'assignee-autocomplete'
                                                        }
                                                    }}
                                                    sx={{
                                                        height: (theme) => theme.spacing(15)
                                                    }}
                                                    value={assigneeSearch}
                                                    variant="filled"
                                                />
                                            )}
                                        />

                                        {'assigned_user' in errors && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.assigned_user as FieldError).helperText}
                                            </FormHelperText>
                                        )}
                                    </Stack>
                                </div>
                            ),
                            bottomBar: (
                                <BottomBar>
                                    {Object.keys(errors).length > 0 ? (
                                        <ErrorFooterAlert
                                            errors={errors}
                                            requiredOrderedFields={requiredOrderedFields}
                                        />
                                    ) : (
                                        <div className={classes.bottomBar}>
                                            <Button
                                                className={classes.bottomButton}
                                                color="primary"
                                                data-testid="addFollowUpButton"
                                                disabled={showSpinner}
                                                onClick={onSubmit}
                                                variant="contained"
                                            >
                                                {isEditMode
                                                    ? _t('mobile_mobileform_add_follow_up_actions_forms_update_button')
                                                    : _t('mobile_mobileform_add_follow_up_actions_add_follow_up')}
                                            </Button>
                                            <Button
                                                className={classes.bottomButton}
                                                color={isEditMode ? 'error' : 'info'}
                                                onClick={handleDiscardOrDelete}
                                            >
                                                {isEditMode
                                                    ? _t('mobile_drafts_button_delete')
                                                    : _t('mobile_common_button_discard')}
                                            </Button>
                                        </div>
                                    )}
                                </BottomBar>
                            )
                        }}
                    </PageLayout>
                </LocalizationProvider>
            </Dialog>
            <DeleteFollowUpActionModal
                description={_t('mobile_mobileform_add_follow_up_actions_forms_delete_modal_subtitle')}
                handleCloseConfirmation={() => setShowDeleteModal(false)}
                no={_t('mobile_action_sheet_cancel')}
                onSubmit={handleDeleteFollowUpAction}
                showConfirmation={showDeleteModal}
                title={_t('mobile_mobileform_add_follow_up_actions_forms_delete_modal_title')}
                yes={_t('mobile_drafts_button_delete')}
            />
        </>
    );
};
